/**
 * Crew Notification Status — read-only tile on /admin/crew/:id.
 *
 * Answers "why didn't this person get that message". Admins can SEE everything
 * and CHANGE nothing: SMS consent has to come from the handset holder (that is
 * the whole basis of the A2P 10DLC registration), and channel preferences are
 * the crew member's own. There is no write endpoint behind this.
 *
 * All verdicts are computed server-side and rendered here verbatim. Do not
 * re-derive them from raw preferences — the defaults are subtle and the two
 * copies would drift apart. See notificationStatus.controller.ts.
 */

import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  getUserNotificationStatus,
  type CrewNotificationStatus as StatusData,
  type NotificationChannelStatus,
  type NotificationBlockReason,
  type SmsConsentState,
} from '../../api/users';

interface Props {
  userId: string;
}

/** Headline SMS state — the first thing an admin looks for. */
const SMS_STATE: Record<
  SmsConsentState,
  { label: string; tone: string; detail: string }
> = {
  VERIFIED: {
    label: 'Verified',
    tone: 'bg-green-100 text-green-800',
    detail: 'They confirmed a code sent to their handset and agreed to receive texts.',
  },
  AWAITING_VERIFICATION: {
    label: 'Awaiting verification',
    tone: 'bg-yellow-100 text-yellow-800',
    detail:
      'They entered a number but never confirmed the code, so no texts can be sent. They can restart the opt-in from their Profile.',
  },
  OPTED_OUT: {
    label: 'Opted out',
    tone: 'bg-red-100 text-red-800',
    detail:
      'They replied STOP or turned texts off in their Profile. Only they can turn it back on. Do not ask them to be re-added by an admin, because no admin can do it.',
  },
  CONTACT_PHONE_ONLY: {
    label: 'Contact number only',
    tone: 'bg-gray-100 text-gray-700',
    detail:
      'The phone number on file was entered by an admin, which cannot be used for texting. They have to opt in themselves from their Profile.',
  },
  NONE: {
    label: 'No phone number',
    tone: 'bg-gray-100 text-gray-700',
    detail: 'No number on file. Texts are not possible until they add one and verify it.',
  },
};

/** Plain-English reason a channel is blocked, written for an admin to act on. */
const BLOCK_REASON: Record<NotificationBlockReason, string> = {
  ARCHIVED: 'They are archived, so roster-wide notices skip them',
  ORG_TIER: 'Text messages need the Enterprise plan',
  ORG_SMS_OFF: 'Text messages are switched off for this organization',
  ORG_CATEGORY_OFF: 'This organization has these notices switched off',
  NO_CONSENT: 'They have not verified a number for texts',
  OPTED_OUT: 'They opted out of texts',
  AWAITING_VERIFICATION: 'Their number is not verified yet',
  NO_DEVICE: 'No device registered for push',
  PREF_OFF: 'They turned this off',
};

const REMINDER_TIMING_LABEL: Record<string, string> = {
  WEEK_BEFORE: 'One week before',
  DAY_BEFORE: 'The day before',
  NIGHT_BEFORE: 'The night before (8 PM)',
  TWO_HOURS_BEFORE: 'Two hours before',
};

function formatDate(value: string | null): string {
  if (!value) return '-';
  return new Date(value).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
}

/**
 * One cell of the grid. The distinction that matters: a green check means the
 * message actually arrives; a muted mark means the preference is ON but
 * something upstream is blocking it, which is exactly the case an admin is
 * trying to diagnose and the one a plain on/off toggle would hide.
 */
function ChannelCell({ status }: { status: NotificationChannelStatus | null }) {
  if (!status) {
    return (
      <td className="px-3 py-2 text-center text-gray-300" aria-label="Not available">
        -
      </td>
    );
  }

  if (status.deliverable) {
    return (
      <td className="px-3 py-2 text-center">
        <span className="text-green-600 font-semibold" title="Will be delivered">
          ✓
        </span>
      </td>
    );
  }

  const reason = status.blockedBy ? BLOCK_REASON[status.blockedBy] : 'Will not be delivered';
  // A preference that is ON but blocked is worth distinguishing visually from a
  // preference the person simply turned off.
  const blockedUpstream = status.pref && status.blockedBy !== 'PREF_OFF';

  return (
    <td className="px-3 py-2 text-center">
      <span
        className={blockedUpstream ? 'text-amber-600' : 'text-gray-400'}
        title={reason}
        aria-label={reason}
      >
        {blockedUpstream ? '!' : '✕'}
      </span>
    </td>
  );
}

export default function CrewNotificationStatus({ userId }: Props) {
  const [data, setData] = useState<StatusData | null>(null);
  const [error, setError] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(false);

    getUserNotificationStatus(userId)
      .then((result) => {
        if (!cancelled) setData(result);
      })
      .catch(() => {
        if (!cancelled) setError(true);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [userId]);

  if (loading) {
    return (
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <div className="animate-pulse space-y-3">
          <div className="h-5 bg-gray-200 rounded w-1/3" />
          <div className="h-4 bg-gray-200 rounded w-2/3" />
          <div className="h-24 bg-gray-100 rounded" />
        </div>
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <h2 className="text-lg font-semibold text-gray-900 mb-2">Notification Settings</h2>
        <p className="text-sm text-gray-500">Could not load notification settings.</p>
      </div>
    );
  }

  const sms = SMS_STATE[data.sms.state];
  // Surface the org-level blocks once at the top: they explain an entire column
  // of failures at a stroke, and unlike the personal prefs they are fixable by
  // the person reading this page.
  const orgBlocks: Array<{ text: string; to?: string }> = [];
  if (!data.org.smsTierAllowed) {
    orgBlocks.push({
      text: 'Text messages require the Enterprise plan. Nobody in this organization receives them on the current plan.',
      to: '/admin/settings/billing',
    });
  } else if (!data.org.smsNotificationsEnabled) {
    orgBlocks.push({
      text: 'Text messages are switched off for this organization, so nobody receives them.',
      to: '/admin/settings/notifications',
    });
  }
  if (!data.org.notifyOnEventPublished) {
    orgBlocks.push({
      text: 'New-event notices are switched off for this organization.',
      to: '/admin/settings/notifications',
    });
  }
  if (!data.org.notifyOnEventChanges) {
    orgBlocks.push({
      text: 'Event-change notices are switched off for this organization.',
      to: '/admin/settings/notifications',
    });
  }

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
      <div className="mb-1 flex items-baseline justify-between gap-4">
        <h2 className="text-lg font-semibold text-gray-900">Notification Settings</h2>
        <span className="text-xs text-gray-500">View only</span>
      </div>
      <p className="text-sm text-gray-500 mb-5">
        What this person will and won&apos;t receive. Only they can change these, because text
        consent has to come from their own handset.
      </p>

      {/* The grid is per-category, so it cannot express the one thing about an
          archived member that trips admins up: roster-wide sends skip them,
          but a message addressed to them by name still arrives. Said here
          once, rather than crammed into a cell tooltip. */}
      {data.memberState === 'ARCHIVED' && (
        <div className="mb-5 border-l-[3px] border-l-hairline bg-paper px-3 py-2 text-sm text-ink-body">
          This person is archived, so new-event announcements and the org-wide weekly digest skip
          them. Assignments, changes to events they are working, and messages addressed to them by
          name still arrive. Responding to anything puts them back on the roster.
        </div>
      )}

      {data.memberState === 'DEACTIVATED' && (
        <div className="mb-5 border-l-[3px] border-l-ink-muted bg-paper px-3 py-2 text-sm text-ink-body">
          This person is deactivated in this organization, so nothing from it reaches them at all.
          Reactivate them first if they should be hearing from you.
        </div>
      )}

      {/* Text messages */}
      <div className="mb-6">
        <div className="flex flex-wrap items-center gap-2 mb-2">
          <h3 className="text-sm font-medium text-gray-900">Text messages</h3>
          <span className={`px-2 py-0.5 text-xs font-medium ${sms.tone}`}>
            {sms.label}
          </span>
          {data.sms.maskedPhone && (
            <span className="text-xs text-gray-500">{data.sms.maskedPhone}</span>
          )}
        </div>
        <p className="text-sm text-gray-600">{sms.detail}</p>
        {(data.sms.consentedAt || data.sms.optedOutAt) && (
          <dl className="mt-2 flex flex-wrap gap-x-6 gap-y-1 text-xs text-gray-500">
            {data.sms.consentedAt && (
              <div>
                <dt className="inline font-medium">Opted in: </dt>
                <dd className="inline">{formatDate(data.sms.consentedAt)}</dd>
              </div>
            )}
            {data.sms.optedOutAt && (
              <div>
                <dt className="inline font-medium">Opted out: </dt>
                <dd className="inline">{formatDate(data.sms.optedOutAt)}</dd>
              </div>
            )}
          </dl>
        )}
      </div>

      {/* Org-level blocks */}
      {orgBlocks.length > 0 && (
        <div className="mb-6 rounded-md border border-amber-200 bg-amber-50 p-3">
          <p className="text-xs font-medium text-amber-900 mb-1">
            Organization settings are blocking some notifications
          </p>
          <ul className="space-y-1">
            {orgBlocks.map((block) => (
              <li key={block.text} className="text-xs text-amber-800">
                {block.text}{' '}
                {block.to && (
                  <Link to={block.to} className="underline hover:no-underline">
                    Change
                  </Link>
                )}
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Per-category grid */}
      <div className="overflow-x-auto">
        <table className="min-w-full text-sm">
          <caption className="sr-only">
            Notification delivery by category and channel
          </caption>
          <thead>
            <tr className="text-xs uppercase tracking-wide text-gray-500">
              <th scope="col" className="text-left font-medium py-2 pr-3">
                Notification
              </th>
              <th scope="col" className="font-medium px-3 py-2">
                Email
              </th>
              <th scope="col" className="font-medium px-3 py-2">
                Text
              </th>
              <th scope="col" className="font-medium px-3 py-2">
                Push
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {data.categories.map((category) => (
              <tr key={category.key}>
                <th scope="row" className="text-left font-normal text-gray-900 py-2 pr-3">
                  {category.label}
                </th>
                <ChannelCell status={category.email} />
                <ChannelCell status={category.sms} />
                <ChannelCell status={category.push} />
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <p className="mt-3 text-xs text-gray-500">
        <span className="text-green-600 font-semibold">✓</span> delivered ·{' '}
        <span className="text-amber-600 font-semibold">!</span> switched on but blocked ·{' '}
        <span className="text-gray-400 font-semibold">✕</span> switched off. Hover any mark
        for the reason.
      </p>

      {/* Push devices — a pref of "on" with no device is a common dead end. */}
      <div className="mt-5 pt-4 border-t border-gray-100">
        <h3 className="text-sm font-medium text-gray-900 mb-1">Devices for push</h3>
        <p className="text-sm text-gray-600">
          {data.push.deviceCount === 0 ? (
            <>
              No devices registered. Push notifications cannot reach them until they install
              the app and allow notifications.
            </>
          ) : (
            <>
              {data.push.deviceCount} device{data.push.deviceCount === 1 ? '' : 's'}
              {data.push.lastUsedAt && <> · last used {formatDate(data.push.lastUsedAt)}</>}
            </>
          )}
        </p>
      </div>

      {/* Personal event reminders */}
      <div className="mt-5 pt-4 border-t border-gray-100">
        <h3 className="text-sm font-medium text-gray-900 mb-1">Event reminders</h3>
        {data.reminders.length === 0 ? (
          <p className="text-sm text-gray-600">None set up.</p>
        ) : (
          <ul className="space-y-1">
            {data.reminders.map((reminder, index) => (
              <li key={`${reminder.timing}-${reminder.method}-${index}`} className="text-sm text-gray-600">
                {REMINDER_TIMING_LABEL[reminder.timing] ?? reminder.timing} ·{' '}
                {reminder.method === 'sms' ? 'text' : reminder.method}
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}
